import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRightLeft, HardDrive, KeyRound } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { api, ApiError } from "../api/client";
import type { LicenceStatus } from "../api/types";
import { Callout } from "../components/Callout";
import { Dialog, DialogBody } from "../components/Dialog";
import { PropRow } from "../components/panel";
import { Motion, Presence, useClosing } from "../components/presence";
import { Button } from "../components/ui";
import { useAuth } from "../auth/auth";
import { ReauthCancelled, withReauth } from "../auth/admin/reauth";
import { HelpButton } from "../help/HelpButton";
import { t, tServer } from "../i18n/i18n";
import { forgetPoste, formatDate, formatDateTime, formatLicenceDay, LICENCE_QUERY, licenceTypeLabel, reloadForLicence } from "./licence";
import { licenceScope } from "../state/licenceScope";
import { unsavedDrafts, useUnsavedList } from "../state/unsavedDrafts";
import { MOVE_STEPS, MoveCode, MoveConfirm, MovedDialog, MoveExplain, type MoveStep, nextStep, previousStep } from "./MoveLicence";
import { IdentityDialog, identityLabel } from "./IdentityScreen";
import { readOnlyInfo } from "./readOnly";
import { StorageGauge, useStorageDialog } from "./StorageGauge";
import { useStorage } from "../map/zones/useZones";
import { Waiting } from "../components/Waiting";

/** Licence du poste : résumé (pied de la barre latérale, en-tête de l'accueil) et fiche (contrôle, déplacement vers un
 *  autre poste, retrait de la licence de ce poste, révocation de l'accès d'un autre poste). */

function summary(status: LicenceStatus | undefined): { title: string; detail: string } {
  // ui-check: ignore-start — libellé d'attente affiché par <Waiting> là où il est rendu (menu des réglages de la barre latérale)
  if (!status) return { title: t("Licence"), detail: t("vérification…") };
  // ui-check: ignore-end
  if (!status.enforced && !status.licence_code) return { title: t("Licence non exigée"), detail: t("contrôle désactivé sur ce serveur") };
  if (!status.licensed) return { title: t("Licence Generex"), detail: status.message ? tServer(status.message) : t("poste non activé") };
  // Licence expirée, délai de lecture seule en cours (décision d'Alexis du 03/10/2026).
  const readOnly = readOnlyInfo(status);
  if (readOnly) return { title: t("Licence expirée"), detail: t("lecture seule jusqu'au {date}", { date: formatLicenceDay(readOnly.until) }) };
  // Seule la validité s'affiche ici ; type, terminaux et mode hors ligne sont dans la fiche de la licence.
  return { title: t("Licence Generex"), detail: t("jusqu'au {date}", { date: formatDate(status.expires_at) }) };
}

/** État de la licence du poste (même requête que le portail) et son résumé en deux lignes. */
export function useLicenceSummary(): { status: LicenceStatus | undefined; title: string; detail: string } {
  const { data } = useQuery({ queryKey: LICENCE_QUERY, queryFn: api.licence, staleTime: 5 * 60_000 });
  return { status: data, ...summary(data) };
}

/** Puce « Licence Generex » de l'en-tête de l'accueil : ouvre la fiche de la licence ; l'échéance est dans
 *  l'info-bulle. Sous 1280 px (en-tête chargé, tablette en paysage), l'icône seule. */
export function LicenceChip({ onClick }: { onClick: () => void }) {
  const { title, detail } = useLicenceSummary();
  const label = `${title} · ${detail}`;
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="inline-flex h-8 shrink-0 items-center gap-2 rounded-lg px-2.5 text-ui font-medium whitespace-nowrap text-ink transition-colors hover:bg-hover"
    >
      <KeyRound size={16} className="shrink-0" aria-hidden />
      <span className="max-xl:hidden">{title}</span>
    </button>
  );
}

/** Fiche de la licence, montrée et retirée en douceur. `portal` : rendue au niveau du document (depuis la barre
 *  latérale, animée et rognée) ; sans portail, dans le plan de son parent (l'accueil, au-dessus de la carte). */
export function LicenceDialog({ open, onClose, portal = false }: { open: boolean; onClose: () => void; portal?: boolean }) {
  const { status } = useLicenceSummary();
  const dialog = (
    <Presence show={open && Boolean(status)}>{open && status && <LicencePanel status={status} onClose={onClose} />}</Presence>
  );
  return portal ? createPortal(dialog, document.body) : dialog;
}

const TERMINALS_QUERY = ["licence-terminals"] as const;

/** Comptes nominatifs (docs/17 §5.7) : sur une licence en mode comptes, la liste des autres postes et leur libération
 *  sont réservées aux administrateurs de l'unité, avec confirmation du mot de passe ; en mode de repli (comptes
 *  suspendus sur le serveur, §6.5), à personne tant que la licence est ouverte ; ailleurs, rien ne change. */
function useReleaseAllowed(status: LicenceStatus): { accounts: boolean; suspended: boolean; allowed: boolean } {
  const admin = useAuth((s) => s.admin && s.mounted !== null);
  const accounts = status.accounts?.mode === "accounts";
  const suspended = status.accounts?.mode === "fallback";
  return { accounts, suspended, allowed: suspended ? !status.licensed : !accounts || admin };
}

/** Refus d'une libération ou d'une activation, dans la langue choisie : poste tenu par l'unité (409
 *  `SEAT_HELD_BY_UNIT`), sinon le message du serveur. */
export function licenceRefusalText(error: ApiError): string {
  if (error.code === "SEAT_HELD_BY_UNIT")
    return t("Ce poste appartient à l'unité {unite} : seul un administrateur de l'unité connecté sur ce poste peut l'activer sur une autre licence ou le libérer.", {
      unite: typeof error.detail?.unit === "string" ? error.detail.unit : "—",
    });
  if (error.code === "ACCOUNTS_SUSPENDED")
    return t("Comptes suspendus sur ce serveur : aucun poste ne peut être libéré ni repris avant leur retour.");
  return error.message;
}

/** Postes web de la licence (registre F25) : dernière utilisation de chacun ; « Révoquer l'accès » coupe un autre
 *  poste (perdu, cédé, ancien ordinateur) dès sa demande suivante et rend sa place à la licence. */
function TerminalList({ readOnly = false }: { readOnly?: boolean }) {
  const queryClient = useQueryClient();
  const terminals = useQuery({ queryKey: TERMINALS_QUERY, queryFn: api.licenceTerminals, staleTime: 0 });
  const [confirming, setConfirming] = useState<string | null>(null);
  const release = useMutation({
    mutationFn: (ref: string) => withReauth(() => api.releaseLicenceTerminal(ref)),
    onError: (error) => {
      if (error instanceof ReauthCancelled) setConfirming(null);
    },
    onSuccess: (list) => {
      queryClient.setQueryData(TERMINALS_QUERY, list);
      setConfirming(null);
      void queryClient.invalidateQueries({ queryKey: LICENCE_QUERY }); // nombre de terminaux activés
    },
  });
  const failure = release.error instanceof ApiError ? release.error : null;
  if (!terminals.data) return null;
  const target = terminals.data.find((terminal) => terminal.ref === confirming) ?? null;
  const ask = (ref: string) => {
    release.reset();
    setConfirming(ref);
  };
  return (
    <Motion origin="fade" className="border-t border-line pt-3">
      <h3 className="text-ui font-semibold text-ink">{t("Postes web de la licence")}</h3>
      <ul className="mt-1 max-h-52 overflow-y-auto">
        {terminals.data.map((terminal) => (
          <li key={terminal.ref} className="flex min-h-8 items-center justify-between gap-3 border-b border-line py-2 last:border-b-0">
            <span className="min-w-0">
              <span className="block text-ui leading-snug break-words text-ink">
                {terminal.name || t("Poste sans nom")}
                {terminal.current && <span className="ml-1.5 text-ink-muted">{t("(ce poste)")}</span>}
              </span>
              {terminal.holder && <span className="block text-meta leading-snug break-words text-ink-soft">{terminal.holder}</span>}
              <span className="block text-meta leading-snug text-ink-muted">
                {t("Dernière utilisation : {when}", { when: formatDateTime(terminal.last_seen) })}
              </span>
            </span>
            {!terminal.current && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => ask(terminal.ref)}
                disabled={release.isPending}
                title={t("Coupe l'accès de ce poste à la licence et rend sa place")}
              >
                {t("Révoquer l'accès")}
              </Button>
            )}
          </li>
        ))}
      </ul>
      {/* Confirmation explicite, sous la liste : le poste visé, l'effet immédiat ; licence expirée en lecture seule, la
          place rendue ne sert à personne avant le renouvellement, révoquer ne sert qu'à couper l'accès d'un poste perdu
          ou cédé (revue du 03/10/2026). */}
      <Presence show={target !== null}>
        {target && (
          <Callout tone="warn" className="mt-2" title={t("Révoquer l'accès de « {nom} » ?", { nom: target.name || t("Poste sans nom") })}>
            <p>{t("Ce poste perd aussitôt l'accès à la licence et à ses dossiers ; sa place redevient libre.")}</p>
            {readOnly && (
              <p className="mt-1">
                {t("Licence expirée : le poste révoqué ne pourra être réactivé qu'après le renouvellement de la licence, et sa place ne servira à aucun autre poste d'ici là. Ne révoquez que l'accès d'un poste perdu ou cédé.")}
              </p>
            )}
            <div className="mt-2 flex justify-end gap-2">
              <Button size="sm" variant="secondary" onClick={() => setConfirming(null)} disabled={release.isPending}>
                {t("Annuler")}
              </Button>
              <Button size="sm" variant="danger" onClick={() => release.mutate(target.ref)} disabled={release.isPending}>
                {release.isPending ? <Waiting>{t("Révocation…")}</Waiting> : t("Révoquer l'accès")}
              </Button>
            </div>
          </Callout>
        )}
      </Presence>
      <Presence show={Boolean(failure)}>{failure && <Callout tone="danger" className="mt-2">{licenceRefusalText(failure)}</Callout>}</Presence>
      <p className="mt-1.5 text-meta leading-snug text-ink-muted">{t("Les iPad de la licence n'y figurent pas : ils se libèrent depuis l'application Generex pour iPad.")}</p>
    </Motion>
  );
}

/** Fiche « Licence de ce poste » (R9 : fenêtre partagée, Échap, focus) : type, code, poste, échéance ; contrôle,
 *  déplacement guidé vers un autre poste (`MoveLicence`), retrait de la licence de ce poste, révocation de l'accès
 *  d'un autre poste web de la licence. */
function LicencePanel({ status, onClose }: { status: LicenceStatus; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  // Déplacement vers un autre poste : temps en cours de la fenêtre guidée ; code redonné par l'écran final.
  const [step, setStep] = useState<MoveStep | null>(null);
  const [moved, setMoved] = useState<string | null>(null);
  // « Modifier » la personne de ce poste : sa fenêtre remplace la fiche, qui revient à sa fermeture.
  const [editingSeat, setEditingSeat] = useState(false);
  // Sortie de la fiche (Presence de `LicenceDialog`) transmise à la fenêtre, qui a sa propre Presence.
  const closing = useClosing();
  const apply = (next: LicenceStatus) => queryClient.setQueryData(LICENCE_QUERY, next);
  const check = useMutation({ mutationFn: api.checkLicence, onSuccess: apply });
  // Saisies non enregistrées : libérer le poste les efface de ce navigateur, la confirmation le dit.
  const pending = useUnsavedList();
  const asking = confirming || step === "confirm";
  const unsaved = pending.length > 0 || (asking && licenceScope.storedDrafts() > 0);
  const { accounts, suspended, allowed } = useReleaseAllowed(status);
  // Code de licence en clair ou masqué selon la personne connectée (§5.7) : relu à l'ouverture de la fiche.
  useEffect(() => {
    void queryClient.invalidateQueries({ queryKey: LICENCE_QUERY });
  }, [queryClient]);
  const release = useMutation({
    mutationFn: (_: { move: string | null }) => withReauth(() => api.deactivateLicence()),
    onError: (error) => {
      if (error instanceof ReauthCancelled) setConfirming(false);
    },
    onSuccess: (_, { move }) => {
      forgetPoste(); // poste libéré volontairement : pas d'avis de cookie perdu à la prochaine activation
      // Brouillons et état de l'interface de la licence effacés du navigateur, page rechargée sur l'écran
      // d'activation : rien de cette licence ne reste pour la suivante (audit de persistance M8, m6).
      licenceScope.release();
      if (move === null) return reloadForLicence(queryClient);
      // Déplacement : l'écran final redonne le code avant le rechargement ; plus rien ne s'écrit d'ici là.
      licenceScope.freeze();
      unsavedDrafts.close();
      setMoved(move); // code relevé à la confirmation, avant que l'état relu ne l'efface
    },
  });
  const failure = [check.error, release.error].find((e) => e instanceof ApiError) as ApiError | undefined;
  const warning = status.message && status.licence_code && !status.licensed ? tServer(status.message) : null;
  const readOnly = readOnlyInfo(status);
  const moving = step !== null;
  const index = step ? MOVE_STEPS.indexOf(step) : -1;
  const go = (next: MoveStep | null) => {
    release.reset();
    setConfirming(false);
    setStep(next);
  };

  const panelFooter = status.licence_code ? (
    <>
      <span className="mr-auto">
        {!allowed ? null : confirming ? (
          <Button variant="danger" onClick={() => release.mutate({ move: null })} disabled={release.isPending}>
            {release.isPending ? <Waiting>{t("Retrait…")}</Waiting> : t("Confirmer le retrait")}
          </Button>
        ) : (
          <Button variant="ghost" onClick={() => setConfirming(true)} title={t("Ce poste rend sa place à la licence et perd l'accès à ses dossiers")}>
            {t("Retirer la licence de ce poste")}
          </Button>
        )}
      </span>
      <Button variant="secondary" onClick={() => check.mutate()} disabled={check.isPending}>
        {check.isPending ? <Waiting>{t("Vérification…")}</Waiting> : t("Vérifier maintenant")}
      </Button>
    </>
  ) : undefined;

  const moveFooter = (
    <>
      <Button variant="secondary" onClick={() => go(step && previousStep(step))} disabled={release.isPending}>
        {step && previousStep(step) ? t("Retour") : t("Annuler")}
      </Button>
      {step && nextStep(step) ? (
        <Button onClick={() => go(nextStep(step))}>{t("Continuer")}</Button>
      ) : (
        <Button variant="danger" onClick={() => release.mutate({ move: status.licence_code ?? "" })} disabled={release.isPending}>
          {release.isPending ? <Waiting>{t("Libération…")}</Waiting> : t("Libérer ce poste et déplacer la licence")}
        </Button>
      )}
    </>
  );

  return (
    <>
      <Presence show={moved === null && !closing && !editingSeat}>
        {moved === null && !closing && !editingSeat && (
          <Dialog
            title={moving ? t("Déplacer la licence vers un autre poste") : t("Licence de ce poste")}
            subtitle={moving ? t("Étape {n} sur {total}", { n: index + 1, total: MOVE_STEPS.length }) : undefined}
            width={moving ? 480 : 420}
            onClose={onClose}
            actions={<HelpButton screen="poste" buttonSize="sm" />}
            footer={moving ? moveFooter : panelFooter}
          >
            <DialogBody>
              {step === "explain" && <MoveExplain key={step} status={status} readOnly={Boolean(readOnly)} />}
              {step === "code" && <MoveCode key={step} status={status} />}
              {step === "confirm" && <MoveConfirm key={step} />}
              {!moving && (
                <>
                  {status.licence_code ? (
                    <div>
                      <PropRow label={t("Type de licence")}>{licenceTypeLabel(status)}</PropRow>
                      <PropRow label={t("Code")} mono>
                        {status.licence_code}
                      </PropRow>
                      <PropRow label={t("Poste")}>{status.device_name ?? "—"}</PropRow>
                      {status.licence_unit && <PropRow label={t("Unité")}>{status.licence_unit}</PropRow>}
                      {/* Personne qui utilise ce poste (hors mode comptes : c'est alors le compte connecté). */}
                      {status.enforced && status.licensed && !accounts && (
                        <PropRow label={t("Utilisé par")}>
                          <span className="inline-flex items-center gap-2">
                            {identityLabel(status) ?? "—"}
                            <Button size="sm" variant="ghost" onClick={() => setEditingSeat(true)}>
                              {t("Modifier")}
                            </Button>
                          </span>
                        </PropRow>
                      )}
                      <PropRow label={t("Activation")}>{status.source === "offline" ? t("code hors ligne") : t("en ligne")}</PropRow>
                      <PropRow label={t("Terminaux activés")}>
                        {status.activated_devices != null ? t("{active} sur {max}", { active: status.activated_devices, max: status.max_devices }) : "—"}
                      </PropRow>
                      {readOnly ? (
                        <>
                          <PropRow label={t("Expirée le")}>{formatLicenceDay(readOnly.expiredAt)}</PropRow>
                          <PropRow label={t("Lecture seule jusqu'au")}>{formatLicenceDay(readOnly.until)}</PropRow>
                        </>
                      ) : (
                        <PropRow label={t("Expire le")}>{formatDate(status.expires_at)}</PropRow>
                      )}
                      {status.source !== "offline" && <PropRow label={t("Dernier contrôle")}>{formatDateTime(status.checked_at)}</PropRow>}
                      <PropRow label={t("Utilisable hors ligne jusqu'au")}>{formatDate(status.offline_valid_until)}</PropRow>
                    </div>
                  ) : (
                    <p className="text-ui leading-relaxed text-ink-soft">
                      {status.enforced ? t("Ce poste n'est pas activé.") : t("Ce serveur n'exige pas de licence pour l'instant.")}
                    </p>
                  )}
                  {/* Déplacer la licence : l'action principale de la fiche pour qui change d'ordinateur. */}
                  {status.licence_code && allowed && (
                    <Motion origin="fade" className="border-t border-line pt-3">
                      <h3 className="text-ui font-semibold text-ink">{t("Changer d'ordinateur")}</h3>
                      <p className="mt-0.5 text-ui leading-snug text-ink-soft">
                        {t("Ce poste rend sa place, l'autre l'occupe avec le même code ; les dossiers suivent la licence.")}
                      </p>
                      <div className="pt-2.5">
                        <Button onClick={() => go("explain")}>
                          <ArrowRightLeft size={14} /> {t("Déplacer vers un autre poste…")}
                        </Button>
                      </div>
                    </Motion>
                  )}
                  <Presence show={Boolean(readOnly)}>
                    {readOnly && (
                      <Callout tone="info" title={t("Lecture seule jusqu'au {date}", { date: formatLicenceDay(readOnly.until) })}>
                        {/* Poste ouvert par un code hors ligne : jamais contrôlé en ligne, il lui faut un nouveau code (revue du
                            03/10/2026). */}
                        {status.source === "offline"
                          ? t("Les dossiers restent consultables et téléchargeables ; rien ne s'enregistre. Ce poste a été activé par un code hors ligne, qui n'est pas vérifié en ligne : une fois la licence renouvelée, demandez à BELLICOM un nouveau code hors ligne et saisissez-le à l'écran d'activation (« Retirer la licence de ce poste » y mène tout de suite) ; vos dossiers restent dans le même espace. Aucune donnée n'est supprimée.")
                          : t("Les dossiers restent consultables et téléchargeables ; rien ne s'enregistre. Renouvelez la licence auprès de BELLICOM : avec le même code, tout redevient modifiable dès sa vérification, sans rien ressaisir. Aucune donnée n'est supprimée.")}
                      </Callout>
                    )}
                  </Presence>
                  {/* Comptes nominatifs disponibles sur demande (indicateur `on`, licence en mode hérité sans code d'amorçage,
                      docs/17 §8.3). */}
                  {status.accounts?.available && status.accounts.mode === "legacy" && status.accounts.bootstrap === "none" && (
                    <Callout tone="info">{t("Comptes nominatifs disponibles : demandez à BELLICOM le code d'amorçage de votre unité.")}</Callout>
                  )}
                  <LicenceStorage onOpen={onClose} />
                </>
              )}
              <Presence show={asking && Boolean(readOnly)}>
                {asking && readOnly && (
                  <Callout tone="warn">
                    {t("Licence expirée : ce poste ne pourra être réactivé qu'après le renouvellement de la licence.")}
                  </Callout>
                )}
              </Presence>
              <Presence show={asking && unsaved}>
                {asking && unsaved && (
                  <Callout tone="warn" title={t("Des modifications ne sont pas enregistrées")}>
                    {pending.length
                      ? t("{list} : libérer le poste les efface de ce navigateur. Enregistrez-les d'abord si vous voulez les garder.", {
                          list: pending.map((entry) => entry.label).join(" · "),
                        })
                      : t("Des saisies non enregistrées sont gardées sur ce poste : libérer le poste les efface de ce navigateur.")}
                  </Callout>
                )}
              </Presence>
              <Presence show={Boolean(warning) && !moving}>{warning && !moving && <Callout tone="warn">{warning}</Callout>}</Presence>
              <Presence show={Boolean(failure)}>{failure && <Callout tone="danger">{licenceRefusalText(failure)}</Callout>}</Presence>
              {!moving && (
                <>
                  {status.licensed && status.source === "online" && allowed && <TerminalList readOnly={Boolean(readOnly)} />}
                  {status.licensed && accounts && !allowed && (
                    <p className="border-t border-line pt-3 text-meta leading-snug text-ink-muted">
                      {t("Seul un administrateur de l'unité peut déplacer la licence vers un autre poste ou révoquer l'accès d'un poste.")}
                    </p>
                  )}
                  {status.licensed && suspended && (
                    <p className="border-t border-line pt-3 text-meta leading-snug text-ink-muted">
                      {t("Comptes suspendus sur ce serveur : aucun poste ne peut être déplacé, libéré ni repris avant leur retour.")}
                    </p>
                  )}
                </>
              )}
            </DialogBody>
          </Dialog>
        )}
      </Presence>
      <Presence show={editingSeat && !closing}>
        {editingSeat && !closing && <IdentityDialog status={status} onClose={() => setEditingSeat(false)} />}
      </Presence>
      <MovedDialog code={moved} onDone={() => reloadForLicence(queryClient)} />
    </>
  );
}

/** Espace de stockage de la licence dans sa fiche : jauge et accès au détail (zones à supprimer, dossiers). */
export function LicenceStorage({ onOpen }: { onOpen: () => void }) {
  const { data: usage } = useStorage();
  const show = useStorageDialog((s) => s.show);
  return (
    <Presence show={Boolean(usage)}>
      {usage && (
        <Motion origin="fade" className="border-t border-line pt-3">
          <h3 className="mb-1.5 text-ui font-semibold text-ink">{t("Espace de stockage")}</h3>
          <StorageGauge usage={usage} />
          <div className="pt-2.5">
            <Button
              variant="link"
              onClick={() => {
                onOpen();
                show();
              }}
            >
              <HardDrive size={14} /> {t("Voir ce qui occupe l'espace")}
            </Button>
          </div>
        </Motion>
      )}
    </Presence>
  );
}
